/**
 * QSLiveTrackingPanel.jsx
 *
 * Driver marker: crane image (same as OLAMapsForCustomerView.jsx)
 * Pickup pin:    green marker
 * Drop-off pin:  red marker
 * Path:          routed road polyline (OLA Maps routeOptimizer), driver -> pickup,
 *                then pickup -> drop-off once the driver has reached pickup.
 *                The line "erases" behind the driver as they advance, the same
 *                way Ola/Uber consumer apps show only the remaining road ahead.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import polyline from "@mapbox/polyline";
import "./QSLiveTrackingPanel.css";

// Same crane image URL used in OLAMapsForCustomerView
const CRANE_IMG_URL =
  "https://claimpro-dev.s3.ap-south-1.amazonaws.com/Assets/crane/craneonmap.png";

// ── Geo helpers ───────────────────────────────────────────────────────────
function getDistance(coord1, coord2) {
  const [lng1, lat1] = coord1;
  const [lng2, lat2] = coord2;
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findClosestIndex(coord, coords) {
  let minDist = Infinity;
  let closestIndex = -1;
  for (let i = 0; i < coords.length; i++) {
    const d = getDistance(coord, coords[i]);
    if (d < minDist) {
      minDist = d;
      closestIndex = i;
    }
  }
  return closestIndex;
}

function getBearing(start, end) {
  const [lng1, lat1] = start;
  const [lng2, lat2] = end;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.cos(dLon);
  let brng = (Math.atan2(y, x) * 180) / Math.PI;
  brng = (brng + 360) % 360;
  brng = (brng + 91) % 360;
  return brng;
}

const DEVIATION_PROXIMITY_THRESHOLD_M = 100;
const PICKUP_REACHED_THRESHOLD_M = 40;

export default function QSLiveTrackingPanel({
  requestCode,
  adminUserId,
  pickup,
  dropoff,
  driverLocation, // { lat, lng, lastPing } | null
  driverTowardsDestination: driverTowardsDestinationProp, // optional override from backend
  wsRef: externalWsRef,
  onClose,
}) {
  const mapRef              = useRef(null);
  const mapInstanceRef      = useRef(null);
  const olaMapsRef          = useRef(null);
  const driverMarkerRef     = useRef(null);
  const mapInitiatedRef     = useRef(false);

  const fullRouteRef         = useRef([]);   // current full route coords (un-trimmed)
  const hasFetchedRouteRef   = useRef(false);
  const previousDriverCoordRef = useRef(null);
  const previousBearingRef   = useRef(0);
  const isFirstAnimationRef  = useRef(true);
  const driverTowardsDestRef = useRef(false);

  const [mapReady,    setMapReady]    = useState(false);
  const [mapError,    setMapError]    = useState(null);
  const [lastPing,    setLastPing]    = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [driverTowardsDestination, setDriverTowardsDestination] = useState(false);

  // Keep the ref in sync so async callbacks always see the latest phase
  useEffect(() => {
    driverTowardsDestRef.current = driverTowardsDestination;
  }, [driverTowardsDestination]);

  // Allow backend to override the phase directly if it knows better than
  // our local proximity guess (e.g. it has its own "pickup completed" flag).
  useEffect(() => {
    if (typeof driverTowardsDestinationProp === "boolean") {
      setDriverTowardsDestination(driverTowardsDestinationProp);
    }
  }, [driverTowardsDestinationProp]);

  // ── Create crane image marker element ─────────────────────────────────────
  const makeCraneEl = () => {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      transform-origin: center center;
    `;

    const img = document.createElement("img");
    img.src = CRANE_IMG_URL;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
      transform-origin: center center;
      transition: transform 0.2s linear;
    `;

    wrapper.appendChild(img);
    return wrapper;
  };

  // ── Place or move driver marker ───────────────────────────────────────────
  const placeDriverMarker = useCallback((map, olaMaps, lngLat) => {
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLngLat(lngLat);
    } else {
      driverMarkerRef.current = olaMaps
        .addMarker({
          element: makeCraneEl(),
          anchor:  "center",
          offset:  [0, 0],
        })
        .setLngLat(lngLat)
        .addTo(map);
    }
  }, []);

  // ── Draw / replace the route line ─────────────────────────────────────────
  const drawRoute = useCallback((coords) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const routeData = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: coords },
    };
    try {
      const source = map.getSource("route");
      if (source) {
        source.setData(routeData);
      } else {
        map.addSource("route", { type: "geojson", data: routeData });
        map.addLayer({
          id:     "route-line",
          type:   "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint:  {
            "line-color":   "#3b82f6",
            "line-width":   4,
            "line-opacity": 0.9,
          },
        });
      }
    } catch (_) {}
  }, []);

  // ── Fetch a route via OLA routeOptimizer ──────────────────────────────────
  const fetchRoute = useCallback(async (waypoints) => {
    // waypoints: array of [lng, lat]
    try {
      const apiKey = process.env.REACT_APP_OLA_API_KEY;
      const locations = waypoints
        .map(([lng, lat]) => `${lat},${lng}`)
        .join("|");
      const url = `https://api.olamaps.io/routing/v1/routeOptimizer?locations=${locations}&api_key=${apiKey}`;

      const response = await fetch(url, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({}),
      });

      const data = await response.json();
      const encodedPolyline = data?.routes?.[0]?.overview_polyline;
      if (!encodedPolyline) return null;

      const decoded = polyline.decode(encodedPolyline);
      return decoded.map(([lat, lng]) => [lng, lat]);
    } catch (err) {
      console.error("[QSLiveTrackingPanel] route fetch failed:", err);
      return null;
    }
  }, []);

  // ── Load (or reload) the route for the current phase ──────────────────────
  const loadRouteForCurrentPhase = useCallback(
    async (driverLngLat) => {
      if (!pickup?.lat || !pickup?.lng) return;

      const towardsDest = driverTowardsDestRef.current;
      const waypoints = towardsDest
        ? [driverLngLat, [Number(pickup.lng), Number(pickup.lat)], [Number(dropoff.lng), Number(dropoff.lat)]]
        : [driverLngLat, [Number(pickup.lng), Number(pickup.lat)]];

      // Driver -> pickup -> drop-off needs drop-off to exist; if it's the
      // first phase we only need driver -> pickup.
      if (towardsDest && (!dropoff?.lat || !dropoff?.lng)) return;

      const coords = await fetchRoute(waypoints);
      if (!coords) return;

      fullRouteRef.current = coords;
      previousDriverCoordRef.current = driverLngLat;
      hasFetchedRouteRef.current = true;
      drawRoute(coords);
    },
    [pickup, dropoff, fetchRoute, drawRoute]
  );

  // ── Trim the route behind the driver (the "erasing" effect) ──────────────
  // Coordinate-based, not index-based: re-resolves the cut point against
  // whatever fullRouteRef.current currently is, so it stays correct even
  // when called repeatedly mid-animation after earlier trims have already
  // shrunk the array.
  const trimRouteToCoord = useCallback(
    (coord) => {
      const coords = fullRouteRef.current;
      if (!coords || coords.length < 2) return;
      const idx = findClosestIndex(coord, coords);
      if (idx <= 0 || idx >= coords.length) return;
      const trimmed = coords.slice(idx);
      fullRouteRef.current = trimmed;
      drawRoute(trimmed);
    },
    [drawRoute]
  );

  // ── Animate marker between two points along the (trimming) route ─────────
  const animateAlongRoute = useCallback(
    (oldCoord, newCoord, routeCoords, startIndex, endIndex, totalDurationMs = 4000) => {
      const map = mapInstanceRef.current;
      const marker = driverMarkerRef.current;
      if (!map || !marker) return;

      if (isFirstAnimationRef.current) {
        map.setZoom(14);
        isFirstAnimationRef.current = false;
      }

      const isForward = endIndex >= startIndex;
      const segment = isForward
        ? routeCoords.slice(startIndex, endIndex + 1)
        : routeCoords.slice(endIndex, startIndex + 1).reverse();

      if (segment.length < 2) {
        marker.setLngLat(newCoord);
        trimRouteToCoord(newCoord);
        return;
      }

      const distances = [];
      let totalDistance = 0;
      for (let i = 0; i < segment.length - 1; i++) {
        const d = getDistance(segment[i], segment[i + 1]);
        distances.push(d);
        totalDistance += d;
      }
      if (totalDistance === 0) {
        marker.setLngLat(newCoord);
        trimRouteToCoord(newCoord);
        return;
      }

      let currentStep = 0;

      function interpolate(start, end, t) {
        return [
          start[0] + (end[0] - start[0]) * t,
          start[1] + (end[1] - start[1]) * t,
        ];
      }

      function animateStep() {
        if (currentStep >= segment.length - 1) {
          // Fully consumed this segment — trim the underlying route up to
          // the driver's new position so the line stays erased behind them.
          trimRouteToCoord(newCoord);
          return;
        }

        const current = segment[currentStep];
        const next = segment[currentStep + 1];
        const segDist = distances[currentStep];
        const markerEl = marker.getElement();
        const bearing = getBearing(current, next);
        updateMarkerRotation(bearing, markerEl, previousBearingRef);

        const duration = totalDurationMs * (segDist / totalDistance);
        const startTime = performance.now();

        function frame(timestamp) {
          const elapsed = timestamp - startTime;
          const t = Math.min(elapsed / duration, 1);
          const point = interpolate(current, next, t);

          marker.setLngLat(point);
          map.easeTo({ center: point, duration: 80, essential: true });

          // Progressively trim the line as the marker eats through this
          // segment, instead of waiting for the whole segment to finish.
          // Using the marker's own interpolated coordinate (not an index)
          // means this stays correct no matter how many times the
          // underlying route array has already been sliced.
          if (t > 0.5) {
            trimRouteToCoord(point);
          }

          if (t < 1) {
            requestAnimationFrame(frame);
          } else {
            currentStep++;
            animateStep();
          }
        }

        requestAnimationFrame(frame);
      }

      animateStep();
    },
    [trimRouteToCoord]
  );

  function updateMarkerRotation(bearing, markerEl, bearingRef) {
    if (!markerEl || !bearingRef) return;
    const img = markerEl.querySelector("img");
    if (!img) return;

    const imageRotationOffset = 140;
    const rotation = bearing + imageRotationOffset;
    const prevBearing = bearingRef.current;

    let angleDiff = rotation - prevBearing;
    if (angleDiff > 180) angleDiff -= 360;
    if (angleDiff < -180) angleDiff += 360;
    const absDiff = Math.abs(angleDiff);

    if (absDiff < 3) return;

    const smoothFactor = absDiff > 90 ? 0.1 : 0.15;
    const smoothedRotation = prevBearing + angleDiff * smoothFactor;

    img.style.transformOrigin = "center center";
    img.style.transform = `rotate(${smoothedRotation}deg)`;
    bearingRef.current = smoothedRotation;
  }

  // ── WS ping handler ───────────────────────────────────────────────────────
  const handleLocationPing = useCallback(
    async ({ lat, lng, speed = null }) => {
      const map     = mapInstanceRef.current;
      const olaMaps = olaMapsRef.current;
      if (!map || !olaMaps) return;

      const newCoord = [Number(lng), Number(lat)];

      // First-ever ping: just place the marker, route load happens elsewhere.
      if (!driverMarkerRef.current) {
        placeDriverMarker(map, olaMaps, newCoord);
        previousDriverCoordRef.current = newCoord;
        setLastPing({ lat, lng, speed, time: new Date().toISOString() });
        return;
      }

      // Phase transition check: has the driver just reached pickup?
      if (
        !driverTowardsDestRef.current &&
        pickup?.lat != null &&
        pickup?.lng != null
      ) {
        const distToPickup = getDistance(newCoord, [Number(pickup.lng), Number(pickup.lat)]);
        if (distToPickup <= PICKUP_REACHED_THRESHOLD_M) {
          setDriverTowardsDestination(true);
          driverTowardsDestRef.current = true;
          driverMarkerRef.current.setLngLat(newCoord);
          previousDriverCoordRef.current = newCoord;
          setLastPing({ lat, lng, speed, time: new Date().toISOString() });
          // Reload the route for the next leg (pickup -> drop-off)
          await loadRouteForCurrentPhase(newCoord);
          return;
        }
      }

      const routeCoords = fullRouteRef.current;
      const oldCoord = previousDriverCoordRef.current;

      if (!oldCoord || routeCoords.length === 0) {
        driverMarkerRef.current.setLngLat(newCoord);
        previousDriverCoordRef.current = newCoord;
        setLastPing({ lat, lng, speed, time: new Date().toISOString() });
        return;
      }

      const oldIndex = findClosestIndex(oldCoord, routeCoords);
      const newIndex = findClosestIndex(newCoord, routeCoords);

      const isMovingAlongPath = newIndex !== oldIndex;
      const oldProximity = oldIndex !== -1 ? getDistance(oldCoord, routeCoords[oldIndex]) : Infinity;
      const newProximity = newIndex !== -1 ? getDistance(newCoord, routeCoords[newIndex]) : Infinity;
      const isWithinProximity =
        oldProximity < DEVIATION_PROXIMITY_THRESHOLD_M &&
        newProximity < DEVIATION_PROXIMITY_THRESHOLD_M;
      const isOnPath =
        oldIndex !== -1 && newIndex !== -1 && isMovingAlongPath && isWithinProximity;

      previousDriverCoordRef.current = newCoord;
      setLastPing({ lat, lng, speed, time: new Date().toISOString() });

      if (isOnPath) {
        animateAlongRoute(oldCoord, newCoord, routeCoords, oldIndex, newIndex);
        return;
      }

      // Deviated off-route: refetch from current position to the current
      // phase's destination, same pattern as OLAMapsForCustomerView.
      console.warn("[QSLiveTrackingPanel] driver deviated from route", { oldIndex, newIndex });
      const destination = driverTowardsDestRef.current
        ? [Number(dropoff.lng), Number(dropoff.lat)]
        : [Number(pickup.lng), Number(pickup.lat)];

      const newRoute = await fetchRoute([newCoord, destination]);
      if (newRoute) {
        fullRouteRef.current = newRoute;
        drawRoute(newRoute);
        driverMarkerRef.current.setLngLat(newCoord);
      } else {
        driverMarkerRef.current.setLngLat(newCoord);
      }
    },
    [
      placeDriverMarker,
      animateAlongRoute,
      loadRouteForCurrentPhase,
      fetchRoute,
      drawRoute,
      pickup,
      dropoff,
    ]
  );

  // ── 1. Initialise Ola Maps ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadMap = async () => {
      if (!mapRef.current || mapInitiatedRef.current) return;
      mapInitiatedRef.current = true;

      try {
        const { OlaMaps } = await import("olamaps-web-sdk");
        if (cancelled) return;

        const apiKey = process.env.REACT_APP_OLA_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_OLA_API_KEY");

        const olaMaps = new OlaMaps({ apiKey });
        olaMapsRef.current = olaMaps;

        const center =
          driverLocation?.lat != null && driverLocation?.lng != null
            ? [Number(driverLocation.lng), Number(driverLocation.lat)]
            : pickup?.lat != null && pickup?.lng != null
            ? [Number(pickup.lng), Number(pickup.lat)]
            : [77.0679, 28.4354];

        const myMap = olaMaps.init({
          style:
            "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
          container: mapRef.current,
          center,
          zoom: 13,
        });

        myMap.addControl(
          olaMaps.addNavigationControls({
            showCompass:    true,
            showZoom:       true,
            visualizePitch: false,
          })
        );

        mapInstanceRef.current = myMap;

        myMap.on("load", async () => {
          if (cancelled) return;

          // ── Pickup pin — green ──
          if (pickup?.lat && pickup?.lng) {
            olaMaps
              .addMarker({ color: "green", anchor: "bottom", offset: [0, 6] })
              .setLngLat([Number(pickup.lng), Number(pickup.lat)])
              .addTo(myMap);
          }

          // ── Drop-off pin — red ──
          if (dropoff?.lat && dropoff?.lng) {
            olaMaps
              .addMarker({ color: "red", anchor: "bottom", offset: [0, 6] })
              .setLngLat([Number(dropoff.lng), Number(dropoff.lat)])
              .addTo(myMap);
          }

          // ── Initial driver position from DB ──
          if (driverLocation?.lat != null && driverLocation?.lng != null) {
            const driverLngLat = [
              Number(driverLocation.lng),
              Number(driverLocation.lat),
            ];

            placeDriverMarker(myMap, olaMaps, driverLngLat);
            previousDriverCoordRef.current = driverLngLat;

            setLastPing({
              lat:   driverLocation.lat,
              lng:   driverLocation.lng,
              speed: null,
              time:  driverLocation.lastPing
                ? new Date(driverLocation.lastPing).toISOString()
                : new Date().toISOString(),
            });

            // Decide initial phase: if backend didn't tell us, infer it from
            // proximity to pickup vs already past it.
            if (typeof driverTowardsDestinationProp !== "boolean" && pickup?.lat && pickup?.lng) {
              const distToPickup = getDistance(
                driverLngLat,
                [Number(pickup.lng), Number(pickup.lat)]
              );
              if (distToPickup <= PICKUP_REACHED_THRESHOLD_M) {
                setDriverTowardsDestination(true);
                driverTowardsDestRef.current = true;
              }
            }

            // Fetch the route for whichever phase we're in.
            await loadRouteForCurrentPhase(driverLngLat);
          }

          // ── Fit bounds to show all known points ──
          const allLngs = [
            pickup?.lng         ? Number(pickup.lng)         : null,
            dropoff?.lng        ? Number(dropoff.lng)        : null,
            driverLocation?.lng ? Number(driverLocation.lng) : null,
          ].filter((v) => v !== null);

          const allLats = [
            pickup?.lat         ? Number(pickup.lat)         : null,
            dropoff?.lat        ? Number(dropoff.lat)        : null,
            driverLocation?.lat ? Number(driverLocation.lat) : null,
          ].filter((v) => v !== null);

          if (allLats.length >= 2) {
            myMap.fitBounds(
              [
                [Math.min(...allLngs), Math.min(...allLats)],
                [Math.max(...allLngs), Math.max(...allLats)],
              ],
              { padding: 70, maxZoom: 15, duration: 800 }
            );
          } else if (pickup?.lat && pickup?.lng) {
            myMap.setCenter([Number(pickup.lng), Number(pickup.lat)]);
            myMap.setZoom(14);
          }

          if (!cancelled) setMapReady(true);
        });
      } catch (err) {
        console.error("[QSLiveTrackingPanel] Map init:", err);
        if (!cancelled)
          setMapError(
            err.message.includes("REACT_APP_OLA_API_KEY")
              ? "OLA Maps API key missing. Add REACT_APP_OLA_API_KEY to your .env and restart."
              : "Map failed to load. Check your OLA Maps API key."
          );
      }
    };

    const t = setTimeout(loadMap, 100);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

// ── 2. WebSocket subscription ──────────────────────────────────────────────
useEffect(() => {
  if (!adminUserId || !requestCode) return;

  const ws = externalWsRef?.current;

  if (!ws) {
    console.warn(
      "[QSLiveTrackingPanel] Shared websocket not available"
    );
    return;
  }

  const subscribe = () => {
    try {
      ws.send(
        JSON.stringify({
          type: "subscribe-tracking",
          requestCode,
        })
      );

      console.log(
        "[QSLiveTrackingPanel] subscribed:",
        requestCode
      );
    } catch (err) {
      console.error(
        "[QSLiveTrackingPanel] subscribe failed:",
        err
      );
    }
  };
    let handleOpen = null;

  if (ws.readyState === WebSocket.OPEN) {
    setWsConnected(true);
    subscribe();
  } else if (ws.readyState === WebSocket.CONNECTING) {
     handleOpen = () => {
      setWsConnected(true);
      subscribe();
    };

    ws.addEventListener("open", handleOpen, {
      once: true,
    });
  }

  const onMessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (
        data.message === "user-updated-location" &&
        data.accidentVehicleCode === requestCode &&
        data.lat != null &&
        data.lng != null
      ) {
        handleLocationPing({
          lat: Number(data.lat),
          lng: Number(data.lng),
          speed: data.speed_kmh ?? null,
        });
      }
    } catch (err) {
      console.error(
        "[QSLiveTrackingPanel] message parse error",
        err
      );
    }
  };

  const onOpen = () => setWsConnected(true);
  const onClose = () => setWsConnected(false);
  const onError = () => setWsConnected(false);

  ws.addEventListener("message", onMessage);
  ws.addEventListener("open", onOpen);
  ws.addEventListener("close", onClose);
  ws.addEventListener("error", onError);

  return () => {
    try {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: "unsubscribe-tracking",
            requestCode,
          })
        );

        console.log(
          "[QSLiveTrackingPanel] unsubscribed:",
          requestCode
        );
      }
    } catch (err) {
      console.error(
        "[QSLiveTrackingPanel] unsubscribe failed",
        err
      );
    }
    if (handleOpen) {
      ws.removeEventListener("open", handleOpen);
    }

    ws.removeEventListener("message", onMessage);
    ws.removeEventListener("open", onOpen);
    ws.removeEventListener("close", onClose);
    ws.removeEventListener("error", onError);
  };
}, [
  adminUserId,
  requestCode,
  handleLocationPing,
  externalWsRef,
]);

  // ── 3. Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      try { mapInstanceRef.current?.remove(); } catch (_) {}
      mapInstanceRef.current     = null;
      driverMarkerRef.current    = null;
      fullRouteRef.current       = [];
      mapInitiatedRef.current    = false;
      hasFetchedRouteRef.current = false;
    };
  }, []);

  // Add this inside QSLiveTrackingPanel, after the existing effects:
    // useEffect(() => {
    //   if (!externalWsRef?.current) return;
    //   const ws = externalWsRef.current;
    //   setWsConnected(ws.readyState === WebSocket.OPEN);
    // }, [externalWsRef?.current?.readyState]);

  // ─────────────────────────────────────────────────────────────────────────
  const lastSeenStr = lastPing?.time
    ? new Date(lastPing.time).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      })
    : null;

  const speedKmh =
    lastPing?.speed != null ? Math.round(Number(lastPing.speed)) : null;

  const noDriverYet = !driverLocation?.lat && !lastPing;

  return (
    <div className="qs-tracking-panel">

      {/* ── Header ── */}
      <div className="qs-tracking-header">
        <div className="qs-tracking-title">
          <LiveIcon />
          Live tracking
          <span className="qs-tracking-code">{requestCode}</span>
        </div>
        <div className="qs-tracking-meta">
          {lastSeenStr && (
            <span className="qs-tracking-lastseen">Last ping: {lastSeenStr}</span>
          )}
          {speedKmh !== null && (
            <span className="qs-tracking-speed">{speedKmh} km/h</span>
          )}
          <span
            className={`qs-ws-dot ${wsConnected ? "qs-ws-dot--on" : "qs-ws-dot--off"}`}
            title={wsConnected ? "Live" : "Offline"}
          />
        </div>
        {onClose && (
          <button className="qs-tracking-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="qs-tracking-legend-strip">
        <LegendItem color="#22c55e" label="Pickup" />
        <LegendItem color="#ef4444" label="Drop-off" />
        {!noDriverYet && (
          <span className="qs-tracking-legend-item">
            <img
              src={CRANE_IMG_URL}
              alt="Driver"
              style={{ width: 16, height: 16, objectFit: "contain" }}
            />
            Driver
          </span>
        )}
        {!noDriverYet && <LegendItem color="#3b82f6" label="Path" line />}
      </div>

      {/* ── Map ── */}
      <div className="qs-tracking-map-wrap">
        {mapError ? (
          <div className="qs-tracking-map-error">{mapError}</div>
        ) : (
          <>
            <div ref={mapRef} className="qs-tracking-map" />
            {!mapReady && (
              <div className="qs-tracking-map-loading">
                <span className="qs-tracking-spinner qs-tracking-spinner--lg" />
                Loading map…
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Status banners ── */}
      {mapReady && noDriverYet && (
        <div className="qs-tracking-no-data">
          No driver assigned yet — showing pickup &amp; drop-off only.
        </div>
      )}
      {mapReady && !noDriverYet && !wsConnected && (
        <div className="qs-tracking-no-data">
          Showing last known position. Waiting for live updates…
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function LegendItem({ color, label, pulse, line }) {
  return (
    <span className="qs-tracking-legend-item">
      {line ? (
        <span className="qs-tracking-legend-line" style={{ background: color }} />
      ) : (
        <span
          className={`qs-tracking-legend-dot${pulse ? " qs-tracking-legend-dot--pulse" : ""}`}
          style={{ background: color }}
        />
      )}
      {label}
    </span>
  );
}

function LiveIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      <path d="M6.3 6.3a8 8 0 000 11.4M17.7 6.3a8 8 0 010 11.4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6"  y2="18" />
      <line x1="6"  y1="6" x2="18" y2="18" />
    </svg>
  );
}